// src/pages/Products.js
import { useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import Badge from "../components/ui/Badge";
import ProductModal from "../components/ProductModal";
import Modal from "../components/ui/Modal";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  ShoppingBag,
  Package,
  Flame,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

const PLAN_LIMITS = { Free: 5, Advanced: 50, Premium: Infinity };
const CAN_FEATURE = { Free: false, Advanced: true, Premium: true };

export default function Products() {
  const { user } = useAuth();
  const { getProducts, addProduct, updateProduct, deleteProduct } = useApp();
  const products = getProducts(user.id);

  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const limit = PLAN_LIMITS[user.plan] || 5;
  const canFeature = CAN_FEATURE[user.plan] || false;
  const atLimit = products.length >= limit;

  const categories = useMemo(() => {
    const cats = [...new Set(products.map((p) => p.category))];
    return ["All", ...cats];
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q),
      );
    }
    if (filterCat !== "All")
      list = list.filter((p) => p.category === filterCat);
    if (filterStatus !== "All")
      list = list.filter((p) => p.status === filterStatus);
    list.sort((a, b) => {
      let av = a[sortBy],
        bv = b[sortBy];
      if (typeof av === "string") {
        av = av.toLowerCase();
        bv = bv.toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [products, search, filterCat, filterStatus, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const Th = ({ col, label, className = "" }) => (
    <th
      className={`text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 select-none whitespace-nowrap ${className}`}
      onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-1">
        {label}
        {sortBy === col ? (
          sortDir === "asc" ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )
        ) : (
          <ChevronUp size={12} className="opacity-0" />
        )}
      </span>
    </th>
  );

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Product Listings
            <span className="ml-2 text-xs font-normal text-gray-500">
              {products.length}/{limit === Infinity ? "∞" : limit} used
            </span>
          </h2>
          {atLimit && user.plan !== "Premium" && (
            <p className="text-xs text-amber-400 mt-0.5">
              Listing limit reached. Upgrade to add more products.
            </p>
          )}
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          disabled={atLimit}
          className="btn-primary"
          title={atLimit ? "Upgrade to add more products" : "Add product"}>
          <Plus size={16} /> Add Product
        </button>
      </div>

      {/* Filters bar */}
      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            className="input-field pl-9"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            className="input-field w-auto"
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}>
            {categories.map((c) => (
              <option key={c} value={c} className="bg-gray-800">
                {c}
              </option>
            ))}
          </select>
          <select
            className="input-field w-auto"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="All" className="bg-gray-800">
              All Status
            </option>
            <option value="active" className="bg-gray-800">
              Active
            </option>
            <option value="inactive" className="bg-gray-800">
              Inactive
            </option>
            <option value="out_of_stock" className="bg-gray-800">
              Out of Stock
            </option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="border-b border-gray-200 dark:border-gray-800">
              <tr>
                <Th col="name" label="Product" />
                <Th col="category" label="Category" />
                <Th col="price" label="Price" />
                <Th col="stock" label="Stock" />
                <Th col="sales" label="Sales" />
                <Th col="status" label="Status" />
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800/50">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-14 text-center">
                    <Package
                      size={36}
                      className="text-gray-300 dark:text-gray-700 mx-auto mb-3"
                    />
                    <p className="text-sm text-gray-500">
                      {products.length === 0
                        ? "No products yet. Add your first product!"
                        : "No products match your filters."}
                    </p>
                  </td>
                </tr>
              )}
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors group">
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                        <Package
                          size={14}
                          className="text-gray-400 dark:text-gray-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate max-w-[180px]">
                          {p.name}
                        </p>
                        {p.featured && (
                          <span className="flex items-center gap-1 text-[10px] text-amber-400">
                            <Flame size={9} fill="currentColor" /> Featured
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-lg">
                      {p.category}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-sm font-semibold text-gray-800 dark:text-gray-200">
                    ₦{p.price.toLocaleString()}
                  </td>
                  <td className="py-3.5 px-4">
                    <span
                      className={`text-sm font-medium ${p.stock === 0 ? "text-rose-400" : p.stock < 5 ? "text-amber-400" : "text-gray-300"}`}>
                      {p.stock}
                    </span>
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                      <ShoppingBag
                        size={13}
                        className="text-gray-400 dark:text-gray-500"
                      />
                      {p.sales}
                    </div>
                  </td>
                  <td className="py-3.5 px-4">
                    <Badge variant={p.status}>
                      {p.status === "out_of_stock"
                        ? "Out of Stock"
                        : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => {
                          setEditing(p);
                          setModalOpen(true);
                        }}
                        className="p-1.5 text-gray-500 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors"
                        title="Edit">
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(p)}
                        className="p-1.5 text-gray-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                        title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Modal */}
      <ProductModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        product={editing}
        canFeature={canFeature}
        onSave={(p) =>
          editing ? updateProduct(user.id, p) : addProduct(user.id, p)
        }
      />

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Product"
        size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            "{deleteTarget?.name}"
          </span>
          ? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            className="btn-secondary"
            onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={() => {
              deleteProduct(user.id, deleteTarget.id);
              setDeleteTarget(null);
            }}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
